import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, ChevronLeft, ChevronRight, CornerDownRight, FileDown, GalleryHorizontal, List, Loader2, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import ReferenceFrame from '../components/treatment/ReferenceFrame';
import FrameLightbox from '../components/treatment/FrameLightbox';
import ShotNumber from '../components/treatment/ShotNumber';
import GearTag from '../components/treatment/GearTag';
import { useTreatmentPdfExport } from '../components/treatment/useTreatmentPdfExport';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { API_URL } from '../lib/api';
import { saveBlobResponse } from '../lib/download';
import { shotNumbers } from '../lib/treatmentShots';
import { formatRunningTime } from '../lib/treatmentHelpers';
import { cn } from '../lib/utils';
import { Logo } from '../components/Logo';

// The slide arrows, inverted - dark with light icons - so they stand out beside the title
const SLIDE_ARROW = 'h-8 w-8 shrink-0 bg-inverse text-inverse-foreground hover:bg-inverse/85 disabled:opacity-30';

// Section labels on this page: the italic clay eyebrow
const LABEL = 'eyebrow mb-2';

/**
 * One shot: title row, details, then its frames. The list and the slides
 * both draw shots with this; `slide` only makes it larger.
 */
function PublicShot({ shot, number, onOpenFrame, slide = false, stickyTop = null, actions = null }) {
  // Without onOpenFrame (on a phone) a frame is just a picture, not a button
  const Frame = onOpenFrame ? 'button' : 'div';
  const meta = [shot.framing, shot.movement, shot.duration_seconds ? formatRunningTime(shot.duration_seconds) : null, shot.location]
    .filter(Boolean).join(' · ');
  return (
    <>
      {/* Title, details line and gear. Pinned under the bar while the frames
          scroll past - in the list until the next shot pushes it off (it is
          sticky inside its own shot), in slides for the whole slide. */}
      <div
        className={cn(stickyTop !== null && 'sticky z-10 py-2 bg-background')}
        style={stickyTop !== null ? { top: stickyTop } : undefined}
      >
        {/* A hanging indent: the number (and a setup's arrow) in its own column,
            centred on the title's first line; title, details and gear share
            the next column's left edge, however the title wraps. */}
        <div className="flex items-start gap-2">
          <div className={cn('flex items-center gap-2 shrink-0', slide ? 'h-8' : 'h-7')}>
            {shot.parent_id && <CornerDownRight className="w-4 h-4 text-muted-foreground shrink-0" aria-label="Setup" />}
            <ShotNumber number={number} />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            {/* Title, then its details (framing · movement · seconds · location) on the
                same line, wrapping under it when there isn't room */}
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className={cn('font-normal min-w-0', slide ? 'text-[28px] leading-tight' : 'text-[22px] leading-snug')}>{shot.title || 'Untitled'}</h3>
              {meta && <p className="text-sm text-muted-foreground">{meta}</p>}
            </div>
            {shot.equipment.length > 0 && (
              // Tags: our own gear filled, hires outlined
              // Pulled left by a pill's border and padding (11px), less 1px: the
              // title's larger letters sit further inside their box, so this is
              // what lines the first pill's text up with the title by eye
              <ul className="flex flex-wrap gap-1.5 -ml-[10px]" aria-label="Equipment">
                {shot.equipment.map((e, i) => <GearTag key={i} name={e.name} quantity={e.quantity} hire={e.hire} />)}
              </ul>
            )}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
      </div>
      {/* The description scrolls; then the frames across the full width */}
      {shot.description && <p className="whitespace-pre-line max-w-3xl">{shot.description}</p>}
      {shot.references.length > 0 && (
        <div className={cn('grid gap-3 content-start grid-cols-1 sm:grid-cols-2', !slide && 'lg:grid-cols-3')}>
          {shot.references.map((ref, i) => (
            <figure key={ref.id} className="m-0 space-y-1">
              <Frame
                {...(onOpenFrame && {
                  type: 'button',
                  onClick: () => onOpenFrame(i),
                  'aria-label': `View frame ${i + 1} of shot ${number} larger`,
                })}
                className={cn(
                  'group block w-full aspect-[2.39/1] rounded-cards overflow-hidden bg-mist',
                  onOpenFrame && 'cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                )}
              >
                <ReferenceFrame
                  url={ref.image_url}
                  alt={ref.note || `Frame ${i + 1} for shot ${number}`}
                  imgClassName="transition-transform duration-500 ease-in-out group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                />
              </Frame>
              {ref.note && <figcaption className="text-sm italic text-muted-foreground">{ref.note}</figcaption>}
            </figure>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * One shot at a time. Arrow keys, a swipe, or the arrows beside the title
 * step through. Keys are left to the frame viewer while it is open.
 */
function SlideView({ shots, numbers, index, onIndexChange, onOpenFrame, lightboxOpen, stickyTop }) {
  const touchX = useRef(null);
  useEffect(() => {
    if (lightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      if (e.key === 'ArrowRight') onIndexChange(index + 1);
      if (e.key === 'ArrowLeft') onIndexChange(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, onIndexChange, lightboxOpen]);

  const shot = shots[index];
  if (!shot) return null;
  return (
    <section
      aria-roledescription="slide"
      aria-label={`Shot ${numbers[shot.id]}, ${index + 1} of ${shots.length}`}
      className="py-6 space-y-5 min-h-[60vh]"
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const dx = e.changedTouches[0].clientX - (touchX.current ?? e.changedTouches[0].clientX);
        if (Math.abs(dx) > 60) onIndexChange(index + (dx < 0 ? 1 : -1));
        touchX.current = null;
      }}
      data-testid="treatment-slide"
    >
      <PublicShot
        shot={shot}
        number={numbers[shot.id]}
        onOpenFrame={onOpenFrame && ((i) => onOpenFrame(shot.id, i))}
        stickyTop={stickyTop}
        slide
        actions={(
          <>
            <Button size="icon" className={SLIDE_ARROW} aria-label="Previous shot" disabled={index === 0} onClick={() => onIndexChange(index - 1)} data-testid="slide-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="icon" className={SLIDE_ARROW} aria-label="Next shot" disabled={index >= shots.length - 1} onClick={() => onIndexChange(index + 1)} data-testid="slide-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </>
        )}
      />
    </section>
  );
}

/** Own gear first, then hires; each a heading and a list. Empty sections are left out. */
const gearSections = (gear) => [
  ['Gear I have', gear?.own || [], false],
  ['Gear to be hired', gear?.hire || [], true],
].filter(([, items]) => items.length > 0);

function GearList({ heading, items }) {
  return (
    <div>
      <h3 className={LABEL}>{heading}</h3>
      <ul className="divide-y divide-line">
        {items.map((item, i) => (
          <li key={i} className="flex justify-between gap-4 py-2 text-base">
            <span className="min-w-0 break-words">{item.name}</span>
            {item.quantity > 1 && <span className="text-muted-foreground shrink-0">×{item.quantity}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Every item the treatment needs, once, at the end of the page. */
function GearSummary({ gear }) {
  const sections = gearSections(gear);
  if (sections.length === 0) return null;
  return (
    <section className="pt-8 border-t border-line-strong space-y-6" aria-labelledby="gear-summary">
      <h2 id="gear-summary" className="section-title">Gear summary</h2>
      <div className="grid gap-8 sm:grid-cols-2">
        {sections.map(([heading, items]) => <GearList key={heading} heading={heading} items={items} />)}
      </div>
    </section>
  );
}

/** The same summary as a drop-down in the bar: own gear on top, hires below. */
function GearMenu({ gear }) {
  const sections = gearSections(gear);
  if (sections.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" aria-label="Gear summary" data-testid="gear-menu">
          <Package className="w-4 h-4" />
          <span className="hidden sm:inline">Gear</span>
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-96 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto scrollbar-thin space-y-4">
        {sections.map(([heading, items, hire]) => (
          <div key={heading}>
            <h3 className={LABEL}>{heading}</h3>
            <ul className="flex flex-wrap gap-1.5">
              {items.map((item, i) => <GearTag key={i} name={item.name} quantity={item.quantity} hire={hire} />)}
            </ul>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/**
 * A shared treatment, read-only, reached by its link without an account.
 * Shows only what GET /public/treatments/{token} returns, which is built
 * field by field in treatment_service.public_view.
 */
export default function PublicTreatmentPage() {
  const { token } = useParams();
  const [treatment, setTreatment] = useState(null);
  const [missing, setMissing] = useState(false);
  const [viewing, setViewing] = useState(null); // { shotId, index } of the frame shown large
  // List or slides, and which shot a slide shows, live in the address so a
  // link (or a reload) opens on the same shot: ?view=slides&shot=03A
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get('view') === 'slides' ? 'slides' : 'list';
  const barAnchor = useRef(null); // where the bar sits before it sticks
  const bar = useRef(null);
  // Frames open full-size only on a wider screen; on a phone they already fill the width
  const canEnlarge = useMediaQuery('(min-width: 640px)');
  const [barHeight, setBarHeight] = useState(0); // where a shot's pinned title sits

  useEffect(() => {
    axios
      .get(`${API_URL}/public/treatments/${token}`)
      .then((res) => setTreatment(res.data))
      .catch(() => setMissing(true));
  }, [token]);

  const shots = treatment?.shots || [];
  const pdf = useTreatmentPdfExport(shots, async (images) => {
    const response = await axios.post(`${API_URL}/public/treatments/${token}/pdf`, { images }, { responseType: 'blob' });
    saveBlobResponse(response, 'Treatment.pdf', 'application/pdf');
  });

  const numbers = shotNumbers(shots);
  const slideIndex = Math.max(0, shots.findIndex((s) => numbers[s.id] === searchParams.get('shot')));

  // Bring the top of the shots under the bar if the page is scrolled past it.
  const toBar = () => {
    const top = barAnchor.current ? barAnchor.current.getBoundingClientRect().top + window.scrollY : 0;
    if (window.scrollY > top) window.scrollTo({ top });
  };

  // Where the slide's title is on screen when the slide changes. The new
  // slide is scrolled so its title lands in the same place, so stepping
  // through never makes the page jump - even though Approach and Look & feel
  // show above the first slide only.
  const titleAt = useRef(null);
  const slideTitleTop = () => {
    const slide = document.querySelector('[data-testid="treatment-slide"]');
    if (!slide) return null;
    // The title as laid out, not where it is pinned: the slide's top plus its padding
    return slide.getBoundingClientRect().top + parseFloat(window.getComputedStyle(slide).paddingTop);
  };

  const goToSlide = useCallback((i) => {
    if (i < 0 || i >= shots.length) return;
    const slide = document.querySelector('[data-testid="treatment-slide"]');
    // Pinned under the bar (scrolled into the slide) counts as "at the bar"
    titleAt.current = slide ? Math.max(slideTitleTop(), barHeight) : null;
    setSearchParams({ view: 'slides', shot: numbers[shots[i].id] }, { replace: true });
    if (!slide) toBar(); // arriving from the list
  }, [shots, numbers, setSearchParams, barHeight]);

  useLayoutEffect(() => {
    if (titleAt.current === null) return;
    const now = slideTitleTop();
    if (now !== null) window.scrollBy(0, now - titleAt.current);
    titleAt.current = null;
  }, [slideIndex]);

  // Switching view keeps your place: slides open on the shot at the top of
  // the screen, and the list opens scrolled to the slide you were on.
  const showSlides = () => {
    const top = bar.current ? bar.current.getBoundingClientRect().bottom : 0;
    const first = shots.find((s) => {
      const el = document.getElementById(`shot-${numbers[s.id]}`);
      return el && el.getBoundingClientRect().bottom > top + 60;
    });
    goToSlide(Math.max(0, shots.indexOf(first)));
  };
  const showList = () => {
    const at = shots[slideIndex] && numbers[shots[slideIndex].id];
    setSearchParams(at ? { shot: at } : {}, { replace: true });
  };
  useEffect(() => {
    const el = bar.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => setBarHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [treatment]);

  const listShot = mode === 'list' ? searchParams.get('shot') : null;
  useLayoutEffect(() => {
    if (listShot) document.getElementById(`shot-${listShot}`)?.scrollIntoView({ block: 'start' });
  }, [listShot, treatment]);

  if (missing) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center p-6 text-center">
        <div className="space-y-2">
          <h1 className="record-title">This link isn't available</h1>
          <p className="text-muted-foreground">It may have been switched off or replaced. Ask whoever sent it for a new one.</p>
        </div>
      </main>
    );
  }
  if (!treatment) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" aria-label="Loading" />
      </main>
    );
  }

  const viewingShot = shots.find((s) => s.id === viewing?.shotId);

  return (
    <main className="min-h-screen bg-background text-foreground text-[18px] leading-relaxed flex flex-col">
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-8 pt-8 sm:pt-12 pb-20 md:pb-24 space-y-12 md:space-y-20">
        <header className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b border-line-strong">
          <div className="space-y-1 min-w-0">
            <p className="eyebrow">
              {treatment.label || 'Shot list'}{treatment.client ? ` · ${treatment.client}` : ''}
            </p>
            <h1 className="page-title break-words">{treatment.title}</h1>
            {treatment.director && (
              <p className="text-muted-foreground">Directed by {treatment.director}</p>
            )}
          </div>
          {/* The logo stands for the business; the name only shows when there's no logo */}
          {treatment.business_logo ? (
            <img src={treatment.business_logo} alt={treatment.business_name || 'Logo'} className="h-16 w-auto max-w-[200px] object-contain" />
          ) : (
            treatment.business_name && <span className="text-sm text-muted-foreground whitespace-nowrap">{treatment.business_name}</span>
          )}
        </header>

        {shots.length > 0 && (
          <>
            {/* Marks where the bar starts, directly under the header's rule */}
            <div ref={barAnchor} aria-hidden="true" className="!mt-0" />
            {/* Sticks to the top while reading: List / Slides, the slide count, then gear and PDF */}
            {/* Same width and rule as the header's line above it, with even space above and below the buttons */}
            <div ref={bar} className="sticky top-0 z-20 py-4 !mt-0 bg-background border-b border-line-strong grid grid-cols-[auto_1fr_auto] sm:grid-cols-[1fr_auto_1fr] items-center gap-2">
              {/* Left: the view */}
              <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                {/* No shadow on the selected one: it sat under it and made the gap below look 1px tighter */}
                <div role="group" aria-label="View" className="inline-flex rounded-pill p-0.5 bg-mist shrink-0">
                  <Button size="sm" variant={mode === 'list' ? 'secondary' : 'ghost'} aria-pressed={mode === 'list'} aria-label="List" className={cn('shadow-none', mode === 'list' && 'bg-inverse text-inverse-foreground hover:bg-inverse/90')} onClick={showList} data-testid="view-list">
                    <List className="w-4 h-4" /> <span className="hidden sm:inline">List</span>
                  </Button>
                  <Button size="sm" variant={mode === 'slides' ? 'secondary' : 'ghost'} aria-pressed={mode === 'slides'} aria-label="Slides" className={cn('shadow-none', mode === 'slides' && 'bg-inverse text-inverse-foreground hover:bg-inverse/90')} onClick={showSlides} data-testid="view-slides">
                    <GalleryHorizontal className="w-4 h-4" /> <span className="hidden sm:inline">Slides</span>
                  </Button>
                </div>
              </div>
              {/* Middle: which slide, centred on the row */}
              <span className="text-sm tabular-nums text-muted-foreground text-center" aria-live="polite">
                {mode === 'slides' && (
                  <>{slideIndex + 1}<span className="hidden sm:inline"> of </span><span className="sm:hidden">/</span>{shots.length}</>
                )}
              </span>
              {/* Right: what the client can take away */}
              <div className="flex items-center justify-end gap-1 sm:gap-2 shrink-0">
                <GearMenu gear={treatment.gear} />
                <Button variant="outline" size="sm" onClick={pdf.run} disabled={pdf.busy} aria-label="Download PDF" data-testid="public-download-pdf">
                  {pdf.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                  <span className="hidden sm:inline">{pdf.busy ? `${Math.round(pdf.percent)}%` : 'Download PDF'}</span>
                </Button>
              </div>
              {/* PDF progress: a line along the bottom of the bar, and words for screen readers */}
              {pdf.busy && (
                <>
                  <div className="absolute left-0 bottom-0 h-0.5 bg-foreground transition-[width]" style={{ width: `${pdf.percent}%` }} aria-hidden="true" />
                  <span className="sr-only" aria-live="polite">{pdf.label}</span>
                </>
              )}
            </div>
          </>
        )}

        {(treatment.approach || treatment.look) && (
          // Slides after the first leave these out so each shot starts at the top
          <section className={cn('grid gap-10 md:gap-16 sm:grid-cols-2', mode === 'slides' && slideIndex > 0 && 'hidden')}>
            {/* Set as pull-quotes: large and light */}
            {treatment.approach && (
              <div>
                <h2 className={LABEL}>approach</h2>
                <p className="whitespace-pre-line text-[22px] md:text-[26px] font-light leading-snug tracking-tight">{treatment.approach}</p>
              </div>
            )}
            {treatment.look && (
              <div>
                <h2 className={LABEL}>look &amp; feel</h2>
                <p className="whitespace-pre-line text-[22px] md:text-[26px] font-light leading-snug tracking-tight">{treatment.look}</p>
              </div>
            )}
          </section>
        )}

        {mode === 'slides' ? (
          <SlideView
            shots={shots}
            numbers={numbers}
            index={slideIndex}
            onIndexChange={goToSlide}
            onOpenFrame={canEnlarge ? (shotId, i) => setViewing({ shotId, index: i }) : null}
            lightboxOpen={!!viewing}
            stickyTop={barHeight}
          />
        ) : (
          <div>
            {shots.map((shot, index) => (
              <article
                key={shot.id}
                id={`shot-${numbers[shot.id]}`}
                className={cn(
                  'py-8 border-t border-line-strong space-y-5 scroll-mt-16',
                  // Off-screen shots are skipped by the browser's layout and paint
                  // until they come near, so a long treatment scrolls like a short one.
                  '[content-visibility:auto] [contain-intrinsic-size:auto_640px]',
                  // Setups have no rule above them, so a sequence and its setups read as
                  // one block; the arrow before the number marks them.
                  shot.parent_id && 'border-t-0 pt-1',
                  // Tight within a sequence: less space under a row its next setup follows.
                  shots[index + 1]?.parent_id && 'pb-3',
                )}
              >
                <PublicShot
                  shot={shot}
                  number={numbers[shot.id]}
                  onOpenFrame={canEnlarge ? (i) => setViewing({ shotId: shot.id, index: i }) : null}
                  stickyTop={barHeight}
                />
              </article>
            ))}
          </div>
        )}

        <GearSummary gear={treatment.gear} />
      </div>

      {/* The page ends in the one dark band */}
      <footer className="mt-auto bg-inverse text-inverse-foreground rounded-t-[60px] md:rounded-t-curtain px-4 pt-14 md:pt-20 pb-14 md:pb-16">
        <div className="max-w-7xl mx-auto flex flex-col items-center text-center gap-3">
          <Logo onInverse className="w-8 h-8" />
          <p className="text-inverse-muted text-base">
            {treatment.label || 'Shot list'} · {treatment.title}{treatment.business_name ? ` · ${treatment.business_name}` : ''}
          </p>
        </div>
      </footer>
      <FrameLightbox
        frames={viewingShot?.references || []}
        index={canEnlarge ? viewing?.index ?? null : null}
        onIndexChange={(index) => setViewing((v) => (index === null ? null : { ...v, index }))}
        title={viewingShot?.title || 'Untitled'}
        number={viewingShot && numbers[viewingShot.id]}
      />
    </main>
  );
}
